import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import {
  Plus,
  Pencil,
  Search,
  CheckCircle2,
  Power,
  ExternalLink,
  FolderTree,
  ChevronDown,
  ChevronRight,
  X,
  ArrowUpDown,
  SlidersHorizontal,
  List,
  LayoutGrid,
  Image as ImageIcon,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const PAGE_SIZE = 25;

interface BrowseCat {
  id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  display_order: number;
}
interface Classif { id: string; name: string }
interface Trade { id: string; name: string }
interface Unit { id: string; name: string; symbol: string | null }

interface SystemItem {
  id: string;
  name: string;
  description: string | null;
  notes: string | null;
  browse_category_id: string | null;
  category_id: string | null;
  trade_id: string | null;
  unit_id: string | null;
  base_cost: number;
  default_markup_percent: number;
  image_urls: string[];
  is_active: boolean;
  price_last_updated_at: string;
  created_at: string;
  updated_at: string;
}

type StatusFilter = "all" | "active" | "inactive" | "stale" | "overdue";

const formatCurrency = (n: number) =>
  `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function buildBrowsePaths(rows: BrowseCat[]): Map<string, string> {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const cache = new Map<string, string>();
  const resolve = (id: string): string => {
    if (cache.has(id)) return cache.get(id)!;
    const r = byId.get(id);
    if (!r) return "";
    const p = r.parent_id ? `${resolve(r.parent_id)} > ${r.name}` : r.name;
    cache.set(id, p);
    return p;
  };
  rows.forEach((r) => resolve(r.id));
  return cache;
}

interface BrowseNodeProps {
  category: BrowseCat;
  allCategories: BrowseCat[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  depth: number;
}

function BrowseCategoryNode({ category, allCategories, selectedId, onSelect, depth }: BrowseNodeProps) {
  const [expanded, setExpanded] = useState(false);
  const children = allCategories
    .filter((c) => c.parent_id === category.id)
    .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name));
  const hasChildren = children.length > 0;
  const isSelected = selectedId === category.id;

  const paddingClass = ["pl-3", "pl-6", "pl-9", "pl-12"][Math.min(depth, 3)];

  return (
    <div>
      <div
        className={[
          "flex items-center gap-1 pr-2 py-1.5 cursor-pointer text-sm",
          paddingClass,
          isSelected
            ? "border-l-2 border-amber-500 bg-slate-800/60 text-white"
            : "border-l-2 border-transparent text-slate-300 hover:bg-slate-800/40 hover:text-white",
        ].join(" ")}
        onClick={() => onSelect(isSelected ? null : category.id)}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded((p) => !p); }}
            className="shrink-0 text-slate-500 hover:text-slate-300"
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <span className="truncate">{category.name}</span>
      </div>

      {hasChildren && expanded && (
        <div>
          {children.map((child) => (
            <BrowseCategoryNode
              key={child.id}
              category={child}
              allCategories={allCategories}
              selectedId={selectedId}
              onSelect={onSelect}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function SystemLineItems() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { session } = useAuth();
  const adminId = session?.user?.id ?? null;

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialStatus: StatusFilter =
    tabParam === "active" || tabParam === "inactive" || tabParam === "stale" || tabParam === "overdue" || tabParam === "all"
      ? (tabParam as StatusFilter)
      : "active";

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);
  const [browsePanelOpen, setBrowsePanelOpen] = useState(false);
  const [selectedBrowseCategoryId, setSelectedBrowseCategoryId] = useState<string | null>(null);
  const [browseDescendantIds, setBrowseDescendantIds] = useState<string[]>([]);
  const [tradeFilter, setTradeFilter] = useState<string>("__all__");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (statusFilter === "active") next.delete("tab");
    else next.set("tab", statusFilter);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => { setPage(1); }, [debouncedSearch, selectedBrowseCategoryId, tradeFilter, statusFilter, sortBy, sortDir]);

  useEffect(() => {
    if (!selectedBrowseCategoryId) {
      setBrowseDescendantIds([]);
      return;
    }
    supabase
      .rpc('get_browse_category_descendant_ids' as never, { p_category_id: selectedBrowseCategoryId } as never)
      .then(({ data }) => {
        setBrowseDescendantIds((data as string[]) ?? [selectedBrowseCategoryId]);
      });
  }, [selectedBrowseCategoryId]);

  const browseQuery = useQuery({
    queryKey: ["sli-browse-cats"],
    queryFn: async (): Promise<BrowseCat[]> => {
      const { data, error } = await supabase
        .from("browse_categories")
        .select("id, name, parent_id, depth, display_order")
        .is("company_id", null)
        .order("depth", { ascending: true })
        .order("display_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as BrowseCat[];
    },
  });

  const classifQuery = useQuery({
    queryKey: ["sli-classif"],
    queryFn: async (): Promise<Classif[]> => {
      const { data, error } = await supabase
        .from("supplier_categories")
        .select("id, name, is_default, company_id")
        .or("is_default.eq.true,company_id.is.null")
        .order("name");
      if (error) throw error;
      return (data ?? []).map((r) => ({ id: r.id, name: r.name }));
    },
  });

  const tradesQuery = useQuery({
    queryKey: ["sli-trades"],
    queryFn: async (): Promise<Trade[]> => {
      const { data, error } = await supabase
        .from("supplier_trades")
        .select("id, name, is_default, company_id")
        .or("is_default.eq.true,company_id.is.null")
        .order("name");
      if (error) throw error;
      return (data ?? []).map((r) => ({ id: r.id, name: r.name }));
    },
  });

  const unitsQuery = useQuery({
    queryKey: ["sli-units"],
    queryFn: async (): Promise<Unit[]> => {
      const { data, error } = await supabase
        .from("line_item_units")
        .select("id, name, symbol")
        .is("company_id", null)
        .order("name");
      if (error) throw error;
      return (data ?? []) as Unit[];
    },
  });

  const { data: staleThresholds } = useQuery({
    queryKey: ['platform_stale_thresholds'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_platform_stale_thresholds');
      if (error) throw error;
      return data as { amber_days: number; red_days: number };
    },
    staleTime: 5 * 60 * 1000,
  });
  const amberDays = staleThresholds?.amber_days ?? 90;
  const redDays   = staleThresholds?.red_days   ?? 180;

  const browseCats = browseQuery.data ?? [];
  const browsePaths = useMemo(() => buildBrowsePaths(browseCats), [browseCats]);



  const classifs = classifQuery.data ?? [];
  const trades = tradesQuery.data ?? [];
  const units = unitsQuery.data ?? [];

  const classifById = useMemo(() => new Map(classifs.map((c) => [c.id, c])), [classifs]);
  const tradeById = useMemo(() => new Map(trades.map((t) => [t.id, t])), [trades]);
  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);

  const itemsQuery = useQuery({
    queryKey: ["system-line-items", debouncedSearch, selectedBrowseCategoryId, browseDescendantIds, tradeFilter, statusFilter, sortBy, sortDir, page],
    queryFn: async () => {
      let q = supabase
        .from("system_line_items")
        .select("*", { count: "exact" });
      const now = Date.now();
      const dAmber = new Date(now - amberDays * 24 * 60 * 60 * 1000).toISOString();
      const dRed   = new Date(now - redDays   * 24 * 60 * 60 * 1000).toISOString();
      if (statusFilter === "active") q = q.eq("is_active", true);
      else if (statusFilter === "inactive") q = q.eq("is_active", false);
      else if (statusFilter === "stale") q = q.eq("is_active", true).lte("price_last_updated_at", dAmber).gt("price_last_updated_at", dRed);
      else if (statusFilter === "overdue") q = q.eq("is_active", true).lte("price_last_updated_at", dRed);
      if (selectedBrowseCategoryId && browseDescendantIds.length > 0) {
        q = q.in("browse_category_id", browseDescendantIds);
      }
      if (tradeFilter !== "__all__") q = q.eq("trade_id", tradeFilter);
      if (debouncedSearch.trim()) {
        const s = debouncedSearch.trim().replace(/[%,]/g, "");
        q = q.or(`name.ilike.%${s}%,description.ilike.%${s}%`);
      }
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      q = q.order(sortBy, { ascending: sortDir === 'asc' }).range(from, to);
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: (data ?? []) as SystemItem[], total: count ?? 0 };
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel("admin-system-line-items")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "system_line_items" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["system-line-items"] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const [deactivateTarget, setDeactivateTarget] = useState<SystemItem | null>(null);
  const [viewItem, setViewItem] = useState<SystemItem | null>(null);

  const sourcesDetailQuery = useQuery({
    queryKey: ['sli-detail-sources', viewItem?.id],
    enabled: !!viewItem?.id,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('system_line_item_price_sources')
        .select(`
          sort_order,
          source_name,
          platform_supplier_id,
          url,
          last_seen_price,
          last_checked_at,
          platform_suppliers!platform_supplier_id(name)
        `)
        .eq('system_line_item_id', viewItem!.id)
        .order('sort_order');
      if (error) return [];
      return ((data ?? []) as any[]).map((r) => ({
        sort_order: r.sort_order,
        display_name: r.platform_suppliers?.name ?? r.source_name ?? null,
        url: r.url ?? null,
        last_seen_price: r.last_seen_price ?? null,
        last_checked_at: r.last_checked_at ?? null,
      })).filter((r) => !!r.display_name);
    },
  });

  const writeAudit = async (
    action:
      | "system_line_item_deactivated"
      | "system_line_item_activated",
    entityId: string,
    details: Record<string, unknown>
  ) => {
    if (!adminId) return;
    await supabase.from("admin_audit_logs").insert({
      admin_id: adminId,
      action,
      entity_type: "system_line_item",
      entity_id: entityId,
      details: details as never,
    });
  };

  const markReviewed = async (it: SystemItem) => {
    try {
      const { error } = await supabase
        .from("system_line_items")
        .update({ price_last_updated_at: new Date().toISOString(), updated_by: adminId })
        .eq("id", it.id)
        .select();
      if (error) throw error;
      toast.success("Marked as reviewed");
      queryClient.invalidateQueries({ queryKey: ["system-line-items"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    }
  };

  const setActive = async (it: SystemItem, active: boolean) => {
    try {
      const { error } = await supabase
        .from("system_line_items")
        .update({ is_active: active, updated_by: adminId })
        .eq("id", it.id)
        .select();
      if (error) throw error;
      await writeAudit(
        active ? "system_line_item_activated" : "system_line_item_deactivated",
        it.id,
        { name: it.name }
      );
      toast.success(active ? "Item activated" : "Item deactivated");
      queryClient.invalidateQueries({ queryKey: ["system-line-items"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed");
    } finally {
      setDeactivateTarget(null);
    }
  };

  const rows = itemsQuery.data?.rows ?? [];
  const total = itemsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const renderPriceUpdated = (iso: string) => {
    const d = new Date(iso);
    const ageMs = Date.now() - d.getTime();
    const stale = ageMs > amberDays * 24 * 60 * 60 * 1000;
    const rel = formatDistanceToNow(d, { addSuffix: true });
    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={stale ? "text-amber-400" : "text-slate-300"}>{rel}</span>
          </TooltipTrigger>
          <TooltipContent>{format(d, "PPP p")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-white">System Line Items</h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Platform-wide item catalog available to all companies. Companies apply their own
              markup tiers on top of these base costs.
            </p>
          </div>
          <Button onClick={() => navigate("/catalog/system-items/new")} className="shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
        </div>

        {/* Status tabs */}
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="all" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">All</TabsTrigger>
            <TabsTrigger value="active" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">Active</TabsTrigger>
            <TabsTrigger value="inactive" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">Inactive</TabsTrigger>
            <TabsTrigger value="stale" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">Stale ({amberDays}–{redDays}d)</TabsTrigger>
            <TabsTrigger value="overdue" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">Overdue ({redDays}+d)</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="rounded-md border border-slate-800 overflow-hidden flex flex-col">
          {/* Toolbar row — inside the card border, above browse panel and table */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800 bg-slate-900/60">
            {/* Browse toggle */}
            <Button
              variant="outline"
              onClick={() => setBrowsePanelOpen((prev) => !prev)}
              className="shrink-0 border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white h-9"
            >
              <FolderTree className="h-4 w-4 mr-2" />
              Browse
              {selectedBrowseCategoryId && (
                <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-semibold">1</span>
              )}
            </Button>

            {/* Search input */}
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or description…"
                className="pl-8 h-9 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
              />
            </div>

            {/* Sort controls */}
            <div className="flex items-center gap-1">
              <Select value={sortBy} onValueChange={(val) => { setSortBy(val); setPage(1); }}>
                <SelectTrigger className="h-9 w-[160px] bg-slate-900 border-slate-700 text-slate-100">
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="base_cost">Base Cost</SelectItem>
                  <SelectItem value="price_last_updated_at">Price Updated</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 px-2 text-xs text-slate-400 hover:text-white"
                onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
              >
                {sortDir === 'asc' ? 'Asc' : 'Desc'}
              </Button>
            </div>

            {/* Filters popover */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="shrink-0 border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white h-9"
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Filters
                  {(tradeFilter !== '__all__') && (
                    <span className="ml-2 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-semibold">
                      1
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 bg-slate-900 border-slate-700 text-slate-100" align="end">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Filters</div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Trade</div>
                    <Select value={tradeFilter} onValueChange={setTradeFilter}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-100">
                        <SelectValue placeholder="All trades" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                        <SelectItem value="__all__">All trades</SelectItem>
                        {trades.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(tradeFilter !== '__all__') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-slate-400 hover:text-white"
                      onClick={() => { setTradeFilter('__all__'); setPage(1); }}
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* View toggle */}
            <div className="flex items-center gap-1 ml-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('list')}
                className={`h-9 w-9 p-0 ${viewMode === 'list' ? 'text-white bg-slate-800' : 'text-slate-500 hover:text-slate-200'}`}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('grid')}
                className={`h-9 w-9 p-0 ${viewMode === 'grid' ? 'text-white bg-slate-800' : 'text-slate-500 hover:text-slate-200'}`}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>

            {/* Active browse breadcrumb */}
            {selectedBrowseCategoryId && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-amber-300 truncate max-w-[280px]">
                  Browsing: {browsePaths.get(selectedBrowseCategoryId) ?? '—'}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setSelectedBrowseCategoryId(null); setPage(1); }}
                  className="h-5 w-5 text-slate-500 hover:text-slate-300"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>

          {/* Browse panel + content — flex row */}
          <div className="flex overflow-hidden">
            {/* Left browse panel — only rendered when open */}
            {browsePanelOpen && (
              <aside className="w-64 shrink-0 border-r border-slate-800 bg-slate-900/40 flex flex-col max-h-[calc(100vh-16rem)]">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-slate-300">
                    <FolderTree className="h-3.5 w-3.5" />
                    Browse
                  </div>
                  {selectedBrowseCategoryId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-slate-400 hover:text-white"
                      onClick={() => { setSelectedBrowseCategoryId(null); setPage(1); }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <div className="overflow-y-auto py-1 flex-1">
                  <div
                    className={[
                      "pl-3 pr-2 py-1.5 cursor-pointer text-sm",
                      !selectedBrowseCategoryId
                        ? "border-l-2 border-amber-500 bg-slate-800/60 text-white"
                        : "border-l-2 border-transparent text-slate-300 hover:bg-slate-800/40 hover:text-white",
                    ].join(" ")}
                    onClick={() => { setSelectedBrowseCategoryId(null); setPage(1); }}
                  >
                    All
                  </div>
                  {browseCats
                    .filter((c) => !c.parent_id)
                    .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name))
                    .map((root) => (
                      <BrowseCategoryNode
                        key={root.id}
                        category={root}
                        allCategories={browseCats}
                        selectedId={selectedBrowseCategoryId}
                        onSelect={(id) => { setSelectedBrowseCategoryId(id); setPage(1); }}
                        depth={0}
                      />
                    ))}
                </div>
              </aside>
            )}

            {/* Right content area — list or grid */}
            {viewMode === 'grid' ? (
              <div className="flex-1 min-w-0 overflow-y-auto p-3 max-h-[calc(100vh-16rem)]">
                {itemsQuery.isLoading ? (
                  <div className="py-10 text-center text-slate-400">Loading…</div>
                ) : rows.length === 0 ? (
                  <div className="py-10 text-center text-slate-400">No items found.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {rows.map((it) => {
                      const classif = it.category_id ? classifById.get(it.category_id) : null;
                      const browse = it.browse_category_id ? browsePaths.get(it.browse_category_id) : null;
                      return (
                        <div
                          key={it.id}
                          className="group rounded-md border border-slate-800 bg-slate-900/40 hover:bg-slate-900/70 cursor-pointer overflow-hidden flex flex-col"
                          onClick={() => setViewItem(it)}
                        >
                          {it.image_urls && it.image_urls.length > 0 ? (
                            <div className="aspect-square bg-slate-800/40 overflow-hidden">
                              <img src={it.image_urls[0]} alt="" className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="aspect-square bg-slate-800/40 flex items-center justify-center">
                              <ImageIcon className="h-8 w-8 text-slate-700" />
                            </div>
                          )}
                          <div className="p-3 flex flex-col gap-1.5 flex-1">
                            <div className="font-semibold text-white text-sm truncate">{it.name}</div>
                            {classif && (
                              <span className="self-start text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                                {classif.name}
                              </span>
                            )}
                            {browse && <div className="text-xs text-slate-500 truncate">{browse}</div>}
                            <div className="flex items-center justify-between mt-auto pt-1">
                              <span className="text-white font-mono tabular-nums text-sm">{formatCurrency(Number(it.base_cost))}</span>
                              <span className="text-xs text-slate-500">
                                {formatDistanceToNow(new Date(it.price_last_updated_at), { addSuffix: true })}
                              </span>
                            </div>
                            <div>
                              {it.is_active ? (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Active</span>
                              ) : (
                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-400 border border-slate-700">Inactive</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 min-w-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900/60 text-slate-400">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-medium w-12"></th>
                      <th className="px-3 py-2 font-medium">Name</th>
                      <th className="px-3 py-2 font-medium">Classification</th>
                      <th className="px-3 py-2 font-medium">Browse Category</th>
                      <th className="px-3 py-2 font-medium">Trade</th>
                      <th className="px-3 py-2 font-medium">Unit</th>
                      <th className="px-3 py-2 font-medium text-right">Base Cost</th>
                      <th className="px-3 py-2 font-medium text-right">Default Markup</th>
                      <th className="px-3 py-2 font-medium">Price Updated</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {itemsQuery.isLoading ? (
                      <tr><td colSpan={11} className="py-10 text-center text-slate-400">Loading…</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={11} className="py-10 text-center text-slate-400">
                        No system items found. Add your first item to start building the platform catalog.
                      </td></tr>
                    ) : rows.map((it) => {
                      const classif = it.category_id ? classifById.get(it.category_id) : null;
                      const browse = it.browse_category_id ? browsePaths.get(it.browse_category_id) : null;
                      const trade = it.trade_id ? tradeById.get(it.trade_id) : null;
                      const unit = it.unit_id ? unitById.get(it.unit_id) : null;
                      return (
                        <tr
                          key={it.id}
                          className="group hover:bg-slate-900/40 cursor-pointer"
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('button, a')) return;
                            setViewItem(it);
                          }}
                        >
                          <td className="px-3 py-2">
                            {it.image_urls && it.image_urls.length > 0 ? (
                              <img src={it.image_urls[0]} alt="" className="h-8 w-8 rounded object-cover border border-slate-800" />
                            ) : (
                              <div className="h-8 w-8 rounded bg-slate-800/60 border border-slate-800 flex items-center justify-center">
                                <ImageIcon className="h-3.5 w-3.5 text-slate-600" />
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 font-semibold text-white">
                            {it.name}
                          </td>
                          <td className="px-3 py-2">
                            {classif ? (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                                {classif.name}
                              </span>
                            ) : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-300">{browse ?? <span className="text-slate-600">—</span>}</td>
                          <td className="px-3 py-2 text-slate-300">{trade?.name ?? <span className="text-slate-600">—</span>}</td>
                          <td className="px-3 py-2 text-slate-300">{unit?.symbol ?? unit?.name ?? <span className="text-slate-600">—</span>}</td>
                          <td className="px-3 py-2 text-right text-white font-mono tabular-nums">{formatCurrency(Number(it.base_cost))}</td>
                          <td className="px-3 py-2 text-right text-slate-400">
                            {Number(it.default_markup_percent) > 0 ? `${Number(it.default_markup_percent)}%` : "—"}
                          </td>
                          <td className="px-3 py-2">{renderPriceUpdated(it.price_last_updated_at)}</td>
                          <td className="px-3 py-2">
                            {it.is_active ? (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Active</span>
                            ) : (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-400 border border-slate-700">Inactive</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                              <TooltipProvider delayDuration={150}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => markReviewed(it)}>
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Mark price as current</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => navigate(`/catalog/system-items/${it.id}/edit`)}>
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7"
                                      onClick={() => it.is_active ? setDeactivateTarget(it) : setActive(it, true)}
                                    >
                                      <Power className={`h-3.5 w-3.5 ${it.is_active ? "text-amber-400" : "text-emerald-400"}`} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{it.is_active ? "Deactivate" : "Activate"}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>


        <div className="flex items-center justify-between text-sm text-slate-400">
          <div>{total} item{total === 1 ? "" : "s"}</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <span>Page {page} of {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        </div>
      </div>


      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{viewItem?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">System line item details</DialogDescription>
          </DialogHeader>
          {viewItem && (
            <div className="space-y-4 text-sm">
              {viewItem.description && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Description</div>
                  <div className="text-slate-200 whitespace-pre-wrap">{viewItem.description}</div>
                </div>
              )}
              {viewItem.notes && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Notes</div>
                  <div className="text-slate-200 whitespace-pre-wrap">{viewItem.notes}</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Base Cost</div>
                  <div className="text-white font-mono">{formatCurrency(Number(viewItem.base_cost))}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Default Markup</div>
                  <div className="text-slate-200">{Number(viewItem.default_markup_percent) > 0 ? `${viewItem.default_markup_percent}%` : "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Classification</div>
                  <div className="text-slate-200">{viewItem.category_id ? (classifById.get(viewItem.category_id)?.name ?? "—") : "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Trade</div>
                  <div className="text-slate-200">{viewItem.trade_id ? (tradeById.get(viewItem.trade_id)?.name ?? "—") : "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Unit</div>
                  <div className="text-slate-200">{viewItem.unit_id ? (unitById.get(viewItem.unit_id)?.symbol ?? unitById.get(viewItem.unit_id)?.name ?? "—") : "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Status</div>
                  <div>
                    {viewItem.is_active ? (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">Active</span>
                    ) : (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-400 border border-slate-700">Inactive</span>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Browse Category</div>
                <div className="text-slate-200">{viewItem.browse_category_id ? (browsePaths.get(viewItem.browse_category_id) ?? "—") : "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Price Last Updated</div>
                <div>{renderPriceUpdated(viewItem.price_last_updated_at)}</div>
              </div>
              {sourcesDetailQuery.data && sourcesDetailQuery.data.length > 0 && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Price Sources</div>
                  <div className="space-y-2">
                    {sourcesDetailQuery.data.map((s, i) => (
                      <div key={i} className="flex items-start justify-between gap-2">
                        <div className="flex items-center min-w-0">
                          <span className="text-slate-200 truncate">{s.display_name}</span>
                          {s.url && (
                            <a
                              href={s.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-amber-400 hover:text-amber-300 ml-2 shrink-0"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          {s.last_seen_price && (
                            <div className="text-xs text-slate-300">
                              Last seen: R {Number(s.last_seen_price).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                            </div>
                          )}
                          {s.last_checked_at && (
                            <div className={`text-xs ${
                              Date.now() - new Date(s.last_checked_at).getTime() > amberDays * 24 * 60 * 60 * 1000
                                ? 'text-amber-400'
                                : 'text-slate-500'
                            }`}>
                              Checked: {new Date(s.last_checked_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {viewItem.image_urls && viewItem.image_urls.length > 0 && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Images</div>
                  <div className="grid grid-cols-3 gap-2">
                    {viewItem.image_urls.map((url, i) => (
                      <img key={i} src={url} alt="" className="w-full aspect-square object-cover rounded border border-slate-800" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setViewItem(null)}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
            >
              Close
            </Button>
            <Button
              onClick={() => { navigate(`/catalog/system-items/${viewItem?.id}/edit`); setViewItem(null); }}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
            >
              Edit Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deactivateTarget} onOpenChange={(o) => { if (!o) setDeactivateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate '{deactivateTarget?.name}'?</AlertDialogTitle>
            <AlertDialogDescription>
              This item will no longer appear in the estimate catalog for any company. Existing estimates that already contain this item are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deactivateTarget && setActive(deactivateTarget, false)}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
